import { zodResolver } from '@hookform/resolvers/zod/dist/zod'
import { LoadingButton } from '@mui/lab'
import { Box, Grid, Stack } from '@mui/material'
import { updateAdminOrAnalyst } from 'api/users'
import FormCheckbox from 'components/form-elements/FormCheckbox'
import FormInput from 'components/form-elements/FormInput'
import FormPhone from 'components/form-elements/FormPhone'
import FormSelect from 'components/form-elements/FormSelect'
import NotificationSys from 'components/NotificationSystem'
import { EMAIL_IS_INVALID, FIELD_IS_REQUIRED } from 'const'
import { useUnlicensedConfirm } from 'features/User/hooks/useUnlicensedConfirm'
import { useCallback } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useMutation } from 'react-query'
import { UpdateAdminOrAnalystRequest, UserModel, UserType, UserTypeLabels } from 'types'
import { boolean, object, string, TypeOf } from 'zod'
import { UserAdminWelcomeButtons } from '../UserAdminWelcomeButtons'
import { UserDetailsBreadcrumbs } from '../UserDetailsBreadcrumbs/UserDetailsBreadcrumbs'

const formSchema = object({
  firstname: string().min(1, FIELD_IS_REQUIRED),
  lastname: string().min(1, FIELD_IS_REQUIRED),
  email: string().email(EMAIL_IS_INVALID),
  role: string().min(1, FIELD_IS_REQUIRED),
  isActive: boolean(),
  phoneNumber: string(),
})

type FormType = TypeOf<typeof formSchema>

export function UserAdminDetailsForm({
  user,
  onAfterUpdate,
}: {
  user: UserModel
  onAfterUpdate: () => void
}) {
  const defaultValues = {
    firstname: user.firstname,
    lastname: user.lastname,
    email: user.email,
    role: user.type[0],
    isActive: !user.isUnlicensed,
    phoneNumber: user.phoneNumber,
  }

  const methods = useForm<FormType>({
    mode: 'all',
    resolver: zodResolver(formSchema),
    defaultValues,
  })

  const { formState, watch } = methods
  const emailValue = watch('email')
  const isEmailDirty = emailValue !== user.email

  const saveUserMutation = useMutation(updateAdminOrAnalyst)

  const getRequestData = useCallback(() => {
    const values = methods.getValues()

    const data: UpdateAdminOrAnalystRequest = {
      id: user.id,
      name: values.firstname,
      lastName: values.lastname,
      email: values.email,
      type: [values.role as UserType],
      isUnlicensed: !values.isActive,
      phoneNumber: values.phoneNumber,
    }

    return data
  }, [methods, user])

  const adminTypes = [
    { label: UserTypeLabels[UserType.ADMIN], value: UserType.ADMIN },
    { label: UserTypeLabels[UserType.ANALYST], value: UserType.ANALYST },
  ]

  const handleUpdate = useCallback(async () => {
    const request = getRequestData()
    await saveUserMutation.mutateAsync(request)
    NotificationSys.showSuccess('User updated')
    onAfterUpdate()
  }, [getRequestData, onAfterUpdate, saveUserMutation])

  const { unlicensedConfirm } = useUnlicensedConfirm()

  const confirmText = `Please confirm that you want to ${
    user.isUnlicensed ? 'activate' : 'deactivate'
  } this user`

  return (
    <>
      <UserDetailsBreadcrumbs user={user} />

      <FormProvider {...methods}>
        <Box
          component="form"
          onSubmit={methods.handleSubmit(handleUpdate)}
          noValidate
          width="100%"
          mt={4}
        >
          <Grid container spacing={4}>
            <Grid item sm={12} md={12} lg={6} xl={6}>
              <Stack spacing={2}>
                <FormInput label="First Name" name="firstname" required fullWidth />
                <FormInput label="Last Name" name="lastname" required fullWidth />
                <FormInput label="Email" name="email" required fullWidth />
              </Stack>

              <Stack spacing={2} mt={2}>
                <FormCheckbox
                  name="isActive"
                  label="Active"
                  confirmText={confirmText}
                  confirmChangeToTrue={user.isUnlicensed}
                />
              </Stack>

              <Stack spacing={2} mt={2}>
                <FormSelect name="role" label="Role" options={adminTypes} />
              </Stack>

              <Stack spacing={2} mt={2}>
                <FormPhone label="Mobile phone number" name="phoneNumber" fullWidth />
              </Stack>

              <Stack spacing={2} mt={2}>
                <UserAdminWelcomeButtons
                  userId={user.id}
                  isEmailDirty={isEmailDirty}
                  isFormSubmitting={formState.isSubmitting}
                />
              </Stack>
            </Grid>

            <Grid item sm={12} md={12} lg={12} xl={12}>
              <LoadingButton
                disabled={saveUserMutation.isLoading}
                loading={saveUserMutation.isLoading}
                variant="contained"
                type="submit"
                sx={{ width: '120px' }}
              >
                Save
              </LoadingButton>
            </Grid>
          </Grid>
        </Box>

        {unlicensedConfirm}
      </FormProvider>
    </>
  )
}
